
import React, { useState } from 'react';
import { Send, Phone, MapPin, Mail, MessageSquare, Info, ShieldCheck, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    need: 'support',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.need === 'career') {
      navigate('/carrieres');
      return;
    }
    window.location.href = `mailto:contact@primeconnect.biz?subject=Contact Request from ${formData.name}&body=${formData.message} (Service: ${formData.need})`;
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-20">
          <h1 className="text-6xl font-bold text-slate-900 font-heading mb-6 tracking-tight">Parlons de <span className="text-copper-600">votre succès.</span></h1>
          <div className="w-24 h-2 bg-copper-600 rounded-full"></div>
        </header>

        <div className="grid lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2">
            <div className="bg-white border border-sand-300 p-8 md:p-16 rounded-[3.5rem] shadow-2xl shadow-sand-300/30">
              <form onSubmit={handleSubmit} className="space-y-10">
                <div className="grid md:grid-cols-2 gap-10">
                  <div className="space-y-3">
                    <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Nom complet</label>
                    <input 
                      required
                      type="text" 
                      placeholder="Jean Dupont"
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-6 py-5 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Email professionnel</label>
                    <input 
                      required
                      type="email" 
                      placeholder="jean@entreprise.com"
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-6 py-5 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Votre besoin principal</label>
                  <div className="relative">
                    <select 
                      value={formData.need}
                      onChange={(e) => setFormData({...formData, need: e.target.value})}
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-6 py-5 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium appearance-none"
                    >
                      <option value="support">Je veux externaliser mon service client</option>
                      <option value="sales">Je veux faire de la prospection commerciale</option>
                      <option value="career">Je cherche un emploi (Redirection RH)</option>
                      <option value="press">Partenariat / Presse</option>
                    </select>
                    <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
                       <ChevronRight className="rotate-90 text-slate-400" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-bold text-slate-700 uppercase tracking-widest">Détails de votre demande</label>
                  <textarea 
                    rows={6}
                    placeholder="Décrivez votre projet en quelques mots..."
                    className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-6 py-5 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-copper-600 hover:bg-copper-700 py-6 rounded-2xl text-white font-bold text-xl flex items-center justify-center group shadow-2xl shadow-copper-600/30 transition-all transform hover:-translate-y-1"
                >
                  Envoyer la demande
                  <Send className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </button>

                <div className="flex items-start bg-emerald-50 border border-emerald-200 p-6 rounded-[2rem]">
                   <ShieldCheck className="text-emerald-600 w-6 h-6 mr-4 shrink-0 mt-0.5" />
                   <p className="text-sm text-emerald-800 leading-relaxed font-medium italic">
                     "Confidentialité garantie. Juste un échange constructif avec un expert solution pour auditer vos besoins, sans harcèlement commercial."
                   </p>
                </div>
              </form>
            </div>
          </div>

          <div className="space-y-10">
             <div className="bg-slate-900 border border-white/10 p-10 rounded-[3rem] text-white shadow-2xl">
                <h4 className="text-xl font-bold font-heading mb-8 flex items-center">
                  <MessageSquare className="mr-3 w-6 h-6 text-prime-blue" /> Contact Direct
                </h4>
                <div className="space-y-6">
                  <a href="tel:+33757939310" className="flex items-center group">
                    <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mr-4 group-hover:bg-prime-blue transition-all">
                       <Phone className="w-5 h-5 text-prime-blue group-hover:text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-widest">Siège France</p>
                      <p className="font-bold text-lg">+33 7 57 93 93 10</p>
                    </div>
                  </a>
                  <a href="https://wa.me/237696575925" target="_blank" rel="noopener noreferrer" className="flex items-center group">
                    <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mr-4 group-hover:bg-prime-green transition-all">
                       <Phone className="w-5 h-5 text-prime-green group-hover:text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-widest">Siège Cameroun (Tél & WhatsApp)</p>
                      <p className="font-bold text-lg">+237 696 57 59 25</p>
                    </div>
                  </a>
                  <a href="mailto:contact@primeconnect.biz" className="flex items-center group">
                    <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mr-4 group-hover:bg-prime-blue transition-all">
                       <Mail className="w-5 h-5 text-prime-blue group-hover:text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-widest">E-mail</p>
                      <p className="font-bold text-lg">contact@primeconnect.biz</p>
                    </div>
                  </a>
                </div>
             </div>

             <div className="space-y-10 px-6">
                <div className="flex items-start group">
                   <div className="w-10 h-10 bg-sand-100 rounded-xl flex items-center justify-center mr-5 shrink-0 group-hover:bg-prime-blue transition-all group-hover:text-white text-prime-blue">
                      <MapPin size={20} />
                   </div>
                   <div>
                     <p className="text-slate-900 font-bold text-lg font-heading">Paris</p>
                     <p className="text-slate-500 text-sm font-medium leading-relaxed">Centre Stratégique</p>
                   </div>
                </div>
                <div className="flex items-start group">
                   <div className="w-10 h-10 bg-sand-100 rounded-xl flex items-center justify-center mr-5 shrink-0 group-hover:bg-prime-blue transition-all group-hover:text-white text-prime-blue">
                      <MapPin size={20} />
                   </div>
                   <div>
                     <p className="text-slate-900 font-bold text-lg font-heading">Montréal</p>
                     <p className="text-slate-500 text-sm font-medium leading-relaxed">Centre stratégique</p>
                   </div>
                </div>
                <div className="flex items-start group">
                   <div className="w-10 h-10 bg-sand-100 rounded-xl flex items-center justify-center mr-5 shrink-0 group-hover:bg-prime-green transition-all group-hover:text-white text-prime-green">
                      <MapPin size={20} />
                   </div>
                   <div>
                     <p className="text-slate-900 font-bold text-lg font-heading">Yaoundé (Siège)</p>
                     <p className="text-slate-500 text-sm font-medium leading-relaxed italic">Management & Production</p>
                     <p className="text-prime-dark font-bold text-xs mt-1">+237 696 57 59 25</p>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
